import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Layout from '../components/Layout';
import { fetchPost, fetchAllPosts } from '../utils/blogLoader';

const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\[([^\]]+)\]\(([^)]+)\)|!\[([^\]]*)\]\(([^)]+)\))/g);
  return parts.map((part, i) => {
    if (!part) return null;
    const imgInline = part.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgInline) return <Box key={i} component="img" src={imgInline[2]} alt={imgInline[1]} sx={{ maxWidth: '100%', borderRadius: '8px', my: 1 }} />;
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) return <Box key={i} component="a" href={link[2]} target="_blank" rel="noopener noreferrer" sx={{ color: '#6366F1', textDecoration: 'underline' }}>{link[1]}</Box>;
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.replace(/\*\*/g, '')}</strong>;
    return part;
  });
};

const renderContent = (content) => {
  const lines = content.trim().split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Horizontal rule: --- or - - -
    if (/^(-\s*){3,}$/.test(trimmed) || trimmed === '---') {
      elements.push(<Box key={i} sx={{ borderTop: '1px solid #E5E7EB', my: 3 }} />);
      i++; continue;
    }

    // Image: ![alt](url)
    const imgMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      elements.push(
        <Box key={i} component="img" src={imgMatch[2]} alt={imgMatch[1]}
          sx={{ width: '100%', borderRadius: '12px', my: 3, maxHeight: 480, objectFit: 'cover' }} />
      );
      i++; continue;
    }

    // H2
    if (trimmed.startsWith('## ')) {
      elements.push(
        <Typography key={i} component="h2" sx={{ fontWeight: 800, color: '#0F1117', fontSize: { xs: '1.2rem', md: '1.4rem' }, mt: 4, mb: 1.5, letterSpacing: '-0.01em' }}>
          {trimmed.replace('## ', '')}
        </Typography>
      );
      i++; continue;
    }

    // H3
    if (trimmed.startsWith('### ')) {
      elements.push(
        <Typography key={i} component="h3" sx={{ fontWeight: 700, color: '#0F1117', fontSize: { xs: '1rem', md: '1.15rem' }, mt: 3, mb: 1 }}>
          {trimmed.replace('### ', '')}
        </Typography>
      );
      i++; continue;
    }

    // Blockquote: > text
    if (trimmed.startsWith('> ')) {
      elements.push(
        <Box key={i} sx={{ borderLeft: '3px solid #6366F1', pl: 2.5, py: 0.5, my: 2, bgcolor: '#F8F9FF', borderRadius: '0 8px 8px 0' }}>
          <Typography sx={{ color: '#374151', fontStyle: 'italic', lineHeight: 1.8, fontSize: '1rem' }}>
            {renderInline(trimmed.replace('> ', ''))}
          </Typography>
        </Box>
      );
      i++; continue;
    }

    // Numbered list: collect consecutive numbered lines
    if (/^\d+\.\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <Box key={`ol-${i}`} sx={{ pl: 1, mb: 2 }}>
          {items.map((item, j) => (
            <Box key={j} sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
              <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.9rem', minWidth: 20 }}>{j + 1}.</Typography>
              <Typography sx={{ color: '#374151', lineHeight: 1.8, fontSize: '1rem' }}>{renderInline(item)}</Typography>
            </Box>
          ))}
        </Box>
      );
      continue;
    }

    // Bullet list: - or *
    if (/^[-*]\s/.test(trimmed)) {
      elements.push(
        <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366F1', mt: '9px', flexShrink: 0 }} />
          <Typography sx={{ color: '#374151', lineHeight: 1.8, fontSize: '1rem' }}>{renderInline(trimmed.replace(/^[-*]\s/, ''))}</Typography>
        </Box>
      );
      i++; continue;
    }

    // Standalone bold line
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      elements.push(
        <Typography key={i} sx={{ fontWeight: 700, color: '#0F1117', mb: 1 }}>{trimmed.replace(/\*\*/g, '')}</Typography>
      );
      i++; continue;
    }

    // Empty line or line break (\)
    if (trimmed === '' || trimmed === '\\') {
      elements.push(<Box key={i} sx={{ mb: 1 }} />);
      i++; continue;
    }

    // Regular paragraph
    elements.push(
      <Typography key={i} sx={{ color: '#374151', lineHeight: 1.85, fontSize: '1rem', mb: 1 }}>
        {renderInline(trimmed)}
      </Typography>
    );
    i++;
  }

  return elements;
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [otherPosts, setOtherPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    Promise.all([fetchPost(slug), fetchAllPosts()]).then(([p, all]) => {
      setPost(p);
      setOtherPosts(all.filter((x) => x.slug !== slug).slice(0, 3));
      setLoading(false);

      // SEO meta tags
      if (p) {
        document.title = `${p.title} | InfyniHub Blog`;
        const desc = p.excerpt || '';
        const url = `https://infynihub.com/blogs/${p.slug}`;
        const image = p.coverImage || 'https://infynihub.com/new-logo.png';
        document.querySelector('meta[name="description"]')?.setAttribute('content', desc);
        document.querySelector('meta[property="og:title"]')?.setAttribute('content', p.title);
        document.querySelector('meta[property="og:description"]')?.setAttribute('content', desc);
        document.querySelector('meta[property="og:url"]')?.setAttribute('content', url);
        document.querySelector('meta[property="og:image"]')?.setAttribute('content', image);
        document.querySelector('meta[property="og:type"]')?.setAttribute('content', 'article');
        document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', p.title);
        document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', desc);
        document.querySelector('meta[name="twitter:image"]')?.setAttribute('content', image);
        // Canonical
        let canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute('href', url);
      }
    });
  }, [slug]);

  if (loading)
    return (
      <Layout>
        <Box sx={{ pt: '150px', pb: 10, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: '#6366F1' }} />
        </Box>
      </Layout>
    );

  if (!post)
    return (
      <Layout>
        <Box sx={{ pt: '150px', pb: 10, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 900, fontSize: '2rem', color: '#0F1117', mb: 2 }}>Post not found</Typography>
          <Button onClick={() => navigate('/blogs')} startIcon={<ArrowBackIcon />} sx={{ color: '#6366F1' }}>Back to Blogs</Button>
        </Box>
      </Layout>
    );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    author: { '@type': 'Organization', name: post.author || 'InfyniHub Team' },
    publisher: { '@type': 'Organization', name: 'InfyniHub', logo: { '@type': 'ImageObject', url: 'https://infynihub.com/new-logo.png' } },
    datePublished: post.date,
    url: `https://infynihub.com/blogs/${post.slug}`,
    ...(post.coverImage && { image: post.coverImage }),
  };

  return (
    <Layout>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Box sx={{ bgcolor: '#FFFFFF', pt: { xs: '140px', md: '150px' } }}>

        {/* Hero */}
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#F8F9FF', borderBottom: '1px solid #F3F4F6' }}>
          <Container maxWidth="md" sx={{ px: { xs: 3, md: 4 } }}>
            <Button onClick={() => navigate('/blogs')} startIcon={<ArrowBackIcon />}
              sx={{ color: '#6B7280', mb: 2, textTransform: 'none', fontWeight: 600, px: 0, '&:hover': { color: '#6366F1', bgcolor: 'transparent' } }}>
              Back to Blogs
            </Button>
            <Box sx={{ display: 'inline-block', bgcolor: '#EEF2FF', borderRadius: '100px', px: 2, py: 0.5, mb: 2.5 }}>
              <Typography sx={{ color: '#4F46E5', fontSize: '0.75rem', fontWeight: 700 }}>{post.category}</Typography>
            </Box>
            <Typography component="h1" sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.8rem', sm: '2.4rem', md: '2.8rem' }, lineHeight: 1.15, letterSpacing: '-0.02em', mb: 3 }}>
              {post.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: 600 }}>{post.author}</Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#D1D5DB' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                <Typography sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>{post.readTime}</Typography>
              </Box>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#D1D5DB' }} />
              <Typography sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>{post.date}</Typography>
            </Box>
          </Container>
        </Box>

        {/* Cover image */}
        {post.coverImage && (
          <Box sx={{ bgcolor: '#F8F9FF' }}>
            <Container maxWidth="md" sx={{ px: { xs: 3, md: 4 }, pb: 4 }}>
              <Box component="img" src={post.coverImage} alt={post.title}
                sx={{ width: '100%', borderRadius: '16px', maxHeight: 420, objectFit: 'cover' }} />
            </Container>
          </Box>
        )}

        {/* Article body */}
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <Container maxWidth="md" sx={{ px: { xs: 3, md: 4 } }}>
            {renderContent(post.content)}
          </Container>
        </Box>

        {/* CTA */}
        <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#F8F9FF', borderTop: '1px solid #F3F4F6', textAlign: 'center' }}>
          <Container maxWidth="sm" sx={{ px: { xs: 3, md: 4 } }}>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.6rem', md: '2rem' }, letterSpacing: '-0.02em', mb: 2 }}>
              Ready to take action?
            </Typography>
            <Typography sx={{ color: '#6B7280', mb: 4, lineHeight: 1.8 }}>
              Talk to us about your business and we'll help you figure out the right next step.
            </Typography>
            <Button variant="contained" endIcon={<ArrowForwardIcon />}
              onClick={() => { navigate('/'); setTimeout(() => { const el = document.getElementById('contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 300); }}
              sx={{ bgcolor: '#6366F1', color: '#fff', px: 4, py: 1.5, fontWeight: 700, borderRadius: '8px', textTransform: 'none', boxShadow: '0 4px 20px rgba(99,102,241,0.3)', '&:hover': { bgcolor: '#5254CC' } }}>
              Get in Touch
            </Button>
          </Container>
        </Box>

        {/* More posts */}
        {otherPosts.length > 0 && (
          <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#FFFFFF', borderTop: '1px solid #F3F4F6' }}>
            <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
              <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '1.4rem', md: '1.8rem' }, letterSpacing: '-0.02em', mb: 4 }}>More Articles</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)' }, gap: 2.5 }}>
                {otherPosts.map((p) => (
                  <Box key={p.slug} onClick={() => navigate(`/blogs/${p.slug}`)}
                    sx={{ p: 3, bgcolor: '#F8F9FF', border: '1px solid #E5E7EB', borderRadius: '14px', cursor: 'pointer', transition: 'all 0.25s', '&:hover': { borderColor: '#6366F1', transform: 'translateY(-3px)' } }}>
                    <Box sx={{ display: 'inline-block', bgcolor: '#EEF2FF', borderRadius: '100px', px: 1.5, py: 0.3, mb: 1.5 }}>
                      <Typography sx={{ color: '#4F46E5', fontSize: '0.68rem', fontWeight: 700 }}>{p.category}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: '#0F1117', fontSize: '0.9rem', lineHeight: 1.4, mb: 1 }}>{p.title}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>{p.readTime} · {p.date}</Typography>
                  </Box>
                ))}
              </Box>
            </Container>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default BlogPostPage;
